import {
  SECTION_DOLLY_FLOOR,
  SECTION_MIN_DISTANCE,
  aimCameraAt,
  frameAimTarget,
  frameFromSection,
} from '@/shared/sectionFrames'

/**
 * Point the preview camera straight at a frame section.
 *
 * The cylindrical path turns the product to the section's `radial_angle`, which describes a
 * band around an axis. A frame section's radial angle is always the saved default of 0, so
 * that route frames the product's front whatever face the artwork is actually on — for a
 * snapshot, that means a thumbnail of a blank side of the model.
 *
 * Driving the controls is the shared `aimCameraAt`, which the Customizer uses too; its
 * header documents why pointing a camera at a section is more than a setLookAt.
 *
 * `immediate` snaps rather than animates, for a snapshot: an animated move has to be waited
 * out, and a capture that lands a frame early photographs the camera on its way there.
 *
 * Returns false when there is nothing to aim at — no viewer, not a frame profile, or a
 * section with no placement yet — so the caller can fall back to the angle-based path
 * rather than leave the camera wherever it happened to be.
 */
export default async function zoomToFrameSection(section: any, immediate = false): Promise<boolean> {
  const viewer = (window as any).threeViewer
  const controls = viewer?.controls
  const camera = viewer?.camera
  const root = viewer?.product
  if (!controls || !camera || !root) return false
  if (viewer.sectionModel !== 'frame') return false

  const frame = frameFromSection(section)
  if (!frame) return false

  // Give the wheel back to the operator.
  //
  // setUpCameraControls binds it to ACTION.NONE, so scrolling over the preview does nothing
  // at all — whatever the dolly limits say, there is no way to zoom in the admin portal with
  // a mouse. That is defensible for a tumbler, which fills the frame anyway; it is not for a
  // frame profile, where the thing being authored is a couple of centimetres across. Set
  // here rather than in setUpCameraControls so cylindrical profiles keep page scrolling over
  // the canvas exactly as they do today.
  const CameraControls = controls.constructor as any
  if (CameraControls?.ACTION?.DOLLY != null) controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY

  // The dolly floor, because setUpCameraControls pins the range to 0.4-1.5 metres — numbers
  // that suit drinkware, which is what this viewer was built for. A section a couple of
  // centimetres across wants the camera far closer, so that floor would otherwise override
  // the distance asked for here and frame the section from too far out.
  await aimCameraAt(frameAimTarget(frame, root, camera.fov ?? 20, camera.aspect ?? 1), camera, controls, {
    transition: !immediate,
    dollyFloorFactor: SECTION_DOLLY_FLOOR,
    minDistanceAbsolute: SECTION_MIN_DISTANCE,
  })
  return true
}
